import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { prayerLabel, weekdayLabel } from '@/shared/lib/labels';
import { PRAYER_SLOTS, WEEKDAYS, type PrayerSlot, type Weekday } from '@/shared/model/enums';
import { Async } from '@/shared/ui/Async';
import { Button } from '@/shared/ui/Button';
import { Chip } from '@/shared/ui/Chip';

import { useMyAvailability, useSaveAvailability } from '../hooks/use-availability';
import { slotKey, type AvailabilitySlot } from '../model/availability';

export function AvailabilityRoute(): JSX.Element {
  const { t } = useTranslation();
  const query = useMyAvailability();
  return (
    <main className="page stack">
      <h1>{t('availability.title')}</h1>
      <p className="muted">{t('availability.intro')}</p>
      <Async query={query}>{(slots) => <AvailabilityEditor initial={slots} />}</Async>
    </main>
  );
}

function AvailabilityEditor({ initial }: { initial: readonly AvailabilitySlot[] }): JSX.Element {
  const { t } = useTranslation();
  const save = useSaveAvailability();
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initial.map((s) => slotKey(s.weekday, s.prayer))),
  );
  const [dirty, setDirty] = useState(false);

  function toggle(weekday: Weekday, prayer: PrayerSlot): void {
    const key = slotKey(weekday, prayer);
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
    setDirty(true);
  }

  function handleSave(): void {
    const slots: AvailabilitySlot[] = [...selected].map((key) => {
      const [w, p] = key.split(':');
      return { weekday: Number(w) as Weekday, prayer: p as PrayerSlot };
    });
    save.mutate(slots, { onSuccess: () => setDirty(false) });
  }

  return (
    <div className="stack">
      <div className="row row--between">
        <Button onClick={handleSave} loading={save.isPending} disabled={!dirty}>
          {t('common.save')}
        </Button>
        {save.isSuccess && !dirty ? <span className="muted">{t('availability.saved')}</span> : null}
      </div>

      {WEEKDAYS.map((weekday) => {
        // Jumu'ah is a Friday-only slot (weekday 5); it never applies to other days.
        const prayersForDay =
          weekday === 5 ? PRAYER_SLOTS : PRAYER_SLOTS.filter((p) => p !== 'jumuah');
        return (
          <section key={weekday} className="card stack" aria-label={weekdayLabel(t, weekday)}>
            <strong>{weekdayLabel(t, weekday)}</strong>
            <div className="wrap">
              {prayersForDay.map((prayer) => (
                <Chip
                  key={prayer}
                  label={prayerLabel(t, prayer)}
                  selected={selected.has(slotKey(weekday, prayer))}
                  onToggle={() => toggle(weekday, prayer)}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
